import { formatMoney, type Resource, routes } from '@pmai/contracts';
import { Button, Card, ConfirmDialog, EmptyState, IconButton, PageHeader, Spinner } from '@pmai/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from '../../shared/api/hooks';
import { Can } from '../../shared/auth/session';
import { useConfirm } from '../../shared/use-confirm';
import { ResourceDialog } from './resource-dialog';

/** Team members as cards, sorted by name (FR-RES-001…004). */
export function ResourcesPage() {
  const { t } = useTranslation();
  const resources = useApiQuery(routes.resources.list);
  const [editing, setEditing] = useState<Resource | 'new' | null>(null);
  const remove = useConfirm(routes.resources.remove, {
    invalidates: [routes.resources.list],
    params: (r: Resource) => ({ id: r.id }),
  });

  return (
    <>
      <PageHeader
        title={t('resources.title')}
        actions={
          <Can perm="resources.edit">
            <Button icon="plus" onClick={() => setEditing('new')}>
              {t('resources.new')}
            </Button>
          </Can>
        }
      />
      {resources.isPending ? (
        <Spinner label={t('common.loading')} />
      ) : !resources.data?.length ? (
        <EmptyState icon="users" title={t('resources.empty')} />
      ) : (
        <ul aria-label={t('resources.title')} className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {resources.data.map((r) => (
            <li key={r.id}>
              <Card
                title={r.name}
                actions={
                  <Can perm="resources.edit">
                    <div className="flex gap-1">
                      <IconButton
                        icon="pencil"
                        label={`${t('common.edit')}: ${r.name}`}
                        onClick={() => setEditing(r)}
                      />
                      <IconButton
                        icon="trash-2"
                        label={`${t('common.delete')}: ${r.name}`}
                        onClick={() => remove.ask(r)}
                      />
                    </div>
                  </Can>
                }
              >
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
                  <dt className="text-ink-muted">{t('resources.fields.role')}</dt>
                  <dd>{r.role}</dd>
                  {r.availability !== null && (
                    <>
                      <dt className="text-ink-muted">{t('resources.fields.availability')}</dt>
                      <dd>{t('resources.perWeek', { hours: r.availability })}</dd>
                    </>
                  )}
                  {r.competences && (
                    <>
                      <dt className="text-ink-muted">{t('resources.fields.competences')}</dt>
                      <dd className="whitespace-pre-line">{r.competences}</dd>
                    </>
                  )}
                  {r.hourlyRate !== null && (
                    <>
                      <dt className="text-ink-muted">{t('resources.fields.hourlyRate')}</dt>
                      <dd>{t('resources.perHour', { rate: formatMoney(r.hourlyRate) })}</dd>
                    </>
                  )}
                  {r.jiraAccountId && (
                    <>
                      <dt className="text-ink-muted">{t('resources.fields.jiraAccountId')}</dt>
                      <dd className="break-all">{r.jiraAccountId}</dd>
                    </>
                  )}
                </dl>
              </Card>
            </li>
          ))}
        </ul>
      )}
      <ResourceDialog
        open={editing !== null}
        resource={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />
      <ConfirmDialog
        {...remove.dialog}
        title={t('resources.deleteTitle')}
        message={t('resources.deleteText', { name: remove.target?.name ?? '' })}
        confirmLabel={t('common.delete')}
      />
    </>
  );
}
